<script>
import { MATCH_STATUS, GAME_RESULT } from "vue-chess-shared/constants";
import { firestore } from "@/services/firebase";
import GameDataManager from "./GameDataManager";
import { MATCH_DEFAULT, GAME_TYPE } from "@/constants";
import { cachedTimeOffset, serverTimestamp } from "@/utils/firebase-helper";
import { toDateWithOffset } from "vue-chess-shared/utils/date-helper";
import { toAiProfile } from "vue-chess-shared/utils/ai-helper";
import { MatchesAPI } from "@/services/api";
import { delayMs } from "vue-chess-shared/utils";

export default {
  name: "GameDataManagerFirebase",

  extends: GameDataManager,

  data() {
    return {
      match: null,
      moves: null,
    };
  },

  watch: {
    matchId: {
      async handler() {
        if (!this.matchId) return;

        await this.setup();
      },
      immediate: true,
    },

    moves() {
      if (!this.match) return;

      // no need to receive move if createdAt is not yet available in firestore
      if (!this.lastMove || !this.lastMove.createdAt) return;

      this.handleReceiveMove();
    },

    match: {
      deep: true,
      handler() {
        if (this.match.result && !this.ratingUpdateScheduled) {
          // to prevent multiple timeouts
          this.ratingUpdateScheduled = true;
          setTimeout(() => {
            console.log("1.5s later, try to reload sql result");
            this.getMatchFromSql();
          }, 1500);
        }
      },
    },
  },

  methods: {
    async setup() {
      this.matchRef = firestore.collection("matches").doc(this.matchId);
      this.movesRef = this.matchRef.collection("moves");

      try {
        await this.$bind("match", this.matchRef);
        await this.$bind("moves", this.movesRef);
        console.log("game data bind to firestore success");
        this.bindFirestore = true;
      } catch (err) {
        console.log("Cannot bind firestore to get match data");
        console.error(err);
        // No Firebase data, map SQL data to use
        this.bindFirestore = false;
        // cannot bind succesfully from firestore, so try to get from sql
        this.getMatchFromSql();
      }

      this.gameEndedAtLoad = !!this.match.result;
      this.isReady = true;
    },

    async getMatchFromSql() {
      MatchesAPI.getMatch(this.matchId).then(async (data) => {
        if (!data) return;

        await delayMs(2000);

        if (data.ended) {
          // update stats (rating after match and diff)
          this.updatePlayersStats(data);
        }

        // firebase binded successfully
        if (this.match && this.match.createdAt) {
          return;
        }

        this.match = {
          match_id: data.match_id,
          player1: data.player1,
          player2: data.player2,
          gameOverride: data.gameOverride,
          duration: data.duration ? data.duration : null,
          variant: data.variant ? data.variant : MATCH_DEFAULT.VARIANT,
          player_1_score: data.player_1_score / 2,
          player_2_score: data.player_2_score / 2,
          status:
            data.player_1_score + data.player_2_score
              ? MATCH_STATUS.FINISHED
              : MATCH_STATUS.ABORTED,
          result: data.result ? data.result : GAME_RESULT.END,
          createdAt: toDateWithOffset(
            cachedTimeOffset(),
            new Date(data.created)
          ),
          startedAt: toDateWithOffset(
            cachedTimeOffset(),
            new Date(data.started)
          ),
          endedAt: data.ended
            ? toDateWithOffset(cachedTimeOffset(), new Date(data.ended))
            : null,
        };

        if (!this.match.player1) {
          const aiData = data.player_1_id.split("_");
          this.match.player1 = toAiProfile(null, Number(aiData[1]));
        }
        if (!this.match.player2) {
          const aiData = data.player_2_id.split("_");
          this.match.player2 = toAiProfile(null, Number(aiData[1]));
        }

        // Get moves from SQLDB
        await MatchesAPI.getMatchMoves(this.matchId).then((moves) => {
          if (!moves) return;
          let pgn = "";
          // map SQLDB move to Firestore move
          this.moves = moves
            .sort((a, b) => a.move_id - b.move_id)
            .map((move, i) => {
              const s = move.value.split("~");
              pgn += i % 2 ? `${s[2]} ` : `${i / 2 + 1}. ${s[2]} `;
              return {
                id: move.move_id,
                move_number: move.move_number ? move.move_number : i + 1,
                fen: s[0],
                move: s[2],
                pgn: pgn.trim(),
                createdAt: toDateWithOffset(
                  cachedTimeOffset(),
                  new Date(move.created)
                ),
              };
            });
        });
      });
    },

    // update stats (rating after match and diff)
    updatePlayersStats(data) {
      if (data.player1_rating) {
        this.player1_rating_new = data.player1_rating;
      } else {
        this.player1_rating_new = {
          rating: this.player1_rating_current,
        };
      }

      if (data.player2_rating) {
        this.player2_rating_new = data.player2_rating;
      } else {
        this.player2_rating_new = {
          rating: this.player2_rating_current,
        };
      }

      const gameData = this.gameData;
      this.onGameDataUpdated(gameData);
    },

    async createMove(moveData) {
      const { moveNumber, isPremove } = moveData;
      const moveId = `${("000" + moveNumber).slice(-3)}`;

      delete moveData.moveNumber;

      await this.movesRef.doc(moveId).set({
        ...moveData,
        createdAt:
          isPremove && this.lastMove && this.lastMove.receivedAt
            ? this.lastMove.receivedAt
            : serverTimestamp(),
        received: this.gameType === GAME_TYPE.PvAI,
        receivedAt: this.gameType === GAME_TYPE.PvAI ? serverTimestamp() : null,
      });

      // save last move to SQLDB
      let aiPlayerId = null;
      if (this.match.player1.aiLevel) aiPlayerId = this.match.player1.username;
      if (this.match.player2.aiLevel) aiPlayerId = this.match.player2.username;

      const duration = this.lastMoveTime;
      MatchesAPI.postMatchMove(this.matchId, {
        move_number: moveNumber,
        value: [
          moveData.fen,
          `${moveData.orig}-${moveData.dest}`,
          moveData.move,
        ].join("~"),
        duration: duration ? Math.floor(duration * 1000) : 0, // convert to ms
        ai_player_id: aiPlayerId,
      });
    },

    async handleReceiveMove() {
      if (this.match.result) return;
      if (!this.playerColor) return;
      if (!this.lastMove) return;

      // Update received & receivedAt if player is playing
      if (this.lastMove.color === this.opponentColor) {
        // If opponent made the last move, update receivedAt attribute
        if (!this.lastMove.received) {
          this.movesRef.doc(this.lastMove.id).update({
            received: true,
            receivedAt: serverTimestamp(),
          });
        }
      }
    },

    abort() {
      if (!this.playerColor) return;
      if (this.match.result) return;

      this.matchRef.update({
        userAction: {
          abortedByColor: this.playerColor,
          abortedAt: serverTimestamp(),
        },
      });
    },

    resign() {
      if (!this.playerColor) return;
      if (this.match.result) return;

      this.matchRef.update({
        userAction: {
          resignedByColor: this.playerColor,
          resignedAt: serverTimestamp(),
        },
      });
    },

    requestDraw() {
      if (this.match.result) return;
      if (!this.lastMove) return;

      const { userAction } = this.lastMove;
      if (userAction && userAction.drawRequestedAt) return;

      if (!this.lastMove) return;

      this.movesRef.doc(this.lastMove.id).update({
        userAction: {
          drawRequestedByColor: this.playerColor,
          drawRequestedAt: serverTimestamp(),
        },
      });
    },

    denyDraw() {
      if (this.match.result) return;
      if (!this.lastMove) return;

      const { userAction } = this.lastMove;

      // no one request, invalid action
      if (!userAction || !userAction.drawRequestedAt) return;

      if (userAction.drawRespondedAt) return;

      if (!this.lastMove) return;

      this.movesRef.doc(this.lastMove.id).update({
        userAction: {
          ...this.lastMove.userAction,
          drawResponse: "deny",
          drawRespondedAt: serverTimestamp(),
        },
      });
    },

    acceptDraw() {
      if (this.match.result) return;
      if (!this.lastMove) return;

      const { userAction } = this.lastMove;

      // no one request, invalid action
      if (!userAction || !userAction.drawRequestedAt) return;

      if (userAction.drawRespondedAt) return;

      this.movesRef.doc(this.lastMove.id).update({
        userAction: {
          ...this.lastMove.userAction,
          drawResponse: "accept",
          drawRespondedAt: serverTimestamp(),
        },
      });
    },
  },
};
</script>
